import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import {
    Lock,
    Eye,
    EyeOff,
    ShieldCheck,
    ArrowRight,
    KeyRound,
    Info,
    CheckCircle2,
} from "lucide-react";
import { useHeaderAction } from "../../../core/provider/HeaderActionProvider/HeaderAction.js";
import { Button } from "../../../components/ui/Button/index.js";
import { Input } from "../../../components/ui/input/Input.js";
import { toastify } from "../../../components/ui/Toasts/index.js";
import { useChangeAdminPassword } from "../../../core/api/admins/auth/mutation.js";
import { toEnglishNumber } from "../../../lib/toEnglishNumberYup.js";
import { cn } from "../../../lib/cn.js";

// ============================================
// ✅ Schema
// ============================================
const changePasswordSchema = Yup.object().shape({
    oldPassword: Yup.string()
        .required("پسورد فعلی الزامی است")
        .min(4, "حداقل ۴ کاراکتر"),
    newPassword: Yup.string()
        .required("پسورد جدید الزامی است")
        .min(6, "حداقل ۶ کاراکتر")
        .test(
            "not-same",
            "پسورد جدید نباید با پسورد فعلی یکسان باشد",
            function (value) {
                return value !== this.parent.oldPassword;
            }
        ),
    repeatPass: Yup.string()
        .required("تکرار پسورد الزامی است")
        .oneOf([Yup.ref("newPassword")], "پسورد و تکرار آن یکسان نیستند"),
});

// ============================================
// ✅ فیلد پسورد با دکمه نمایش/مخفی
// ============================================
function PasswordField({
    field,
    form,
    label,
    placeholder,
    icon: Icon = Lock,
}: any) {
    const [show, setShow] = useState(false);
    const hasError = form.touched[field.name] && !!form.errors[field.name];

    return (
        <div className="flex flex-col gap-1.5">
            <label className="text-[11px] font-bold text-neutral-700 flex items-center gap-1.5">
                <Icon className="w-3.5 h-3.5 text-neutral-400" />
                {label}
            </label>

            <div className="relative">
                <input
                    {...field}
                    type={show ? "text" : "password"}
                    placeholder={placeholder}
                    dir="ltr"
                    className={cn(
                        "w-full h-11 px-3 pl-11 pr-3 rounded-xl bg-neutral-50 border transition-all text-[12px] font-medium tracking-wider",
                        "placeholder:text-neutral-400 placeholder:tracking-normal placeholder:font-normal",
                        "focus:outline-none focus:ring-4",
                        hasError
                            ? "border-red-300 focus:border-red-500 focus:ring-red-50"
                            : "border-neutral-200 focus:border-brand-500 focus:ring-brand-50"
                    )}
                    autoComplete="new-password"
                />

                <button
                    type="button"
                    onClick={() => setShow((s) => !s)}
                    className="absolute left-1 top-1/2 -translate-y-1/2 w-8 h-8 rounded-lg flex items-center justify-center text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 transition-all"
                    tabIndex={-1}
                >
                    {show ? (
                        <EyeOff className="w-4 h-4" />
                    ) : (
                        <Eye className="w-4 h-4" />
                    )}
                </button>
            </div>

            {hasError && (
                <p className="text-[10px] text-red-600 font-medium flex items-center gap-1">
                    {form.errors[field.name] as string}
                </p>
            )}
        </div>
    );
}

// ============================================
// ✅ کارت نکته امنیتی
// ============================================
function SecurityTip({
    icon: Icon,
    title,
    description,
    color = "brand",
}: {
    icon: any;
    title: string;
    description: string;
    color?: "brand" | "emerald" | "amber";
}) {
    const colors = {
        brand: "bg-brand-50 text-brand-600 border-brand-100",
        emerald: "bg-emerald-50 text-emerald-600 border-emerald-100",
        amber: "bg-amber-50 text-amber-600 border-amber-100",
    };
    return (
        <div
            className={cn(
                "flex items-start gap-2.5 p-3 rounded-xl border",
                colors[color]
            )}
        >
            <div className="flex items-center justify-center w-6 h-6 rounded-lg bg-white/70 shrink-0">
                <Icon className="w-3.5 h-3.5" />
            </div>
            <div className="min-w-0">
                <p className="text-[11px] font-bold">{title}</p>
                <p className="text-[10px] opacity-80 mt-0.5 leading-relaxed">
                    {description}
                </p>
            </div>
        </div>
    );
}

// ============================================
// ✅ کامپوننت اصلی
// ============================================
export default function ChangeAdminPasswordPage() {
    const { setAction } = useHeaderAction();
    const navigate = useNavigate();
    const { mutate: changePassword, isPending } = useChangeAdminPassword();
    const [isSuccess, setIsSuccess] = useState(false);

    // هدر
    useEffect(() => {
        setAction(
            <div className="flex items-center justify-between w-full h-full">
                <div className="flex items-center gap-2.5">
                    <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-brand-50 text-brand-600">
                        <ShieldCheck className="w-4 h-4" />
                    </div>
                    <h1 className="text-sm font-bold text-neutral-900">
                        تغییر پسورد ادمین
                    </h1>
                </div>

                <Button
                    variant="outline"
                    color="neutral"
                    size="sm"
                    rounded="xl"
                    leftIcon={<ArrowRight className="w-3.5 h-3.5" />}
                    onClick={() => navigate(-1)}
                    className="!h-9 !text-[11px]"
                >
                    بازگشت
                </Button>
            </div>
        );
        return () => setAction(null);
    }, [setAction, navigate]);

    // ============================================
    // ✅ Submit
    // ============================================
    const handleSubmit = (values: any, { resetForm }: any) => {
        changePassword(
            {
                oldPassword: toEnglishNumber(values.oldPassword),
                newPassword: toEnglishNumber(values.newPassword),
                repeatPass: toEnglishNumber(values.repeatPass),
            },
            {
                onSuccess: (res: any) => {
                    if (res?.hasError) return;
                    setIsSuccess(true);
                    resetForm();
                    setTimeout(() => setIsSuccess(false), 4000);
                },
            }
        );
    };

    // ============================================
    // ✅ رندر
    // ============================================
    return (
        <div className="flex flex-col h-full">
            <div className="flex-1 min-h-0  overflow-auto">
                <div className="grid grid-cols-1 gap-3 lg:grid-cols-3  mx-auto">
                    {/* ===== کارت فرم (سمت راست) ===== */}
                    <div className="lg:col-span-2 bg-white border rounded-2xl border-neutral-200 overflow-hidden">
                        {/* هدر کارت */}
                        <div className="flex items-center gap-3 px-5 py-4 border-b border-neutral-100">
                            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-brand-500 to-brand-700 text-white shadow-sm shadow-brand-500/30">
                                <KeyRound className="w-5 h-5" />
                            </div>
                            <div>
                                <h3 className="text-[13px] font-bold text-neutral-900">
                                    تغییر رمز عبور
                                </h3>
                                <p className="text-[10px] text-neutral-500 mt-0.5">
                                    برای امنیت حساب، رمز قوی انتخاب کنید
                                </p>
                            </div>
                        </div>

                        {/* فرم */}
                        <Formik
                            initialValues={{
                                oldPassword: "",
                                newPassword: "",
                                repeatPass: "",
                            }}
                            validationSchema={changePasswordSchema}
                            onSubmit={handleSubmit}
                        >
                            {(formik) => (
                                <Form className="p-5 flex flex-col gap-4">
                                    <Field name="oldPassword">
                                        {(p: any) => (
                                            <PasswordField
                                                {...p}
                                                label="رمز عبور فعلی"
                                                placeholder="رمز فعلی خود را وارد کنید"
                                                icon={Lock}
                                            />
                                        )}
                                    </Field>

                                    <div className="h-px bg-neutral-100 my-1" />

                                    <Field name="newPassword">
                                        {(p: any) => (
                                            <PasswordField
                                                {...p}
                                                label="رمز عبور جدید"
                                                placeholder="رمز جدید (حداقل ۶ کاراکتر)"
                                                icon={KeyRound}
                                            />
                                        )}
                                    </Field>

                                    <Field name="repeatPass">
                                        {(p: any) => (
                                            <PasswordField
                                                {...p}
                                                label="تکرار رمز عبور جدید"
                                                placeholder="رمز جدید را دوباره وارد کنید"
                                                icon={ShieldCheck}
                                            />
                                        )}
                                    </Field>

                                    {/* هشدار موفقیت */}
                                    {isSuccess && (
                                        <div className="flex items-center gap-2 p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700">
                                            <CheckCircle2 className="w-4 h-4 shrink-0" />
                                            <p className="text-[11px] font-semibold">
                                                رمز عبور با موفقیت تغییر کرد
                                            </p>
                                        </div>
                                    )}

                                    {/* دکمه‌ها */}
                                    <div className="flex items-center gap-2 mt-2">
                                        <Button
                                            type="button"
                                            variant="outline"
                                            color="neutral"
                                            size="sm"
                                            rounded="xl"
                                            className="w-1/3 !h-10"
                                            onClick={() => formik.resetForm()}
                                            disabled={isPending || !formik.dirty}
                                        >
                                            پاک کردن
                                        </Button>
                                        <Button
                                            type="submit"
                                            variant="solid"
                                            color="primary"
                                            size="sm"
                                            rounded="xl"
                                            className="w-2/3 !h-10"
                                            loading={isPending}
                                            disabled={isPending || !formik.isValid || !formik.dirty}
                                            leftIcon={<ShieldCheck className="w-4 h-4" />}
                                        >
                                            {isPending ? "در حال ذخیره..." : "تغییر رمز عبور"}
                                        </Button>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </div>

                    {/* ===== کارت نکات (سمت چپ) ===== */}
                    <div className="flex flex-col gap-2.5">
                        <div className="bg-white border rounded-2xl border-neutral-200 p-4">
                            <div className="flex items-center gap-2 mb-3">
                                <div className="flex items-center justify-center w-6 h-6 rounded-md bg-brand-50">
                                    <Info className="w-3.5 h-3.5 text-brand-600" />
                                </div>
                                <p className="text-[12px] font-bold text-neutral-800">
                                    توصیه‌های امنیتی
                                </p>
                            </div>

                            <div className="flex flex-col gap-2">
                                <SecurityTip
                                    icon={CheckCircle2}
                                    title="طول مناسب"
                                    description="حداقل ۶ کاراکتر برای رمز عبور در نظر بگیرید."
                                    color="emerald"
                                />
                                <SecurityTip
                                    icon={ShieldCheck}
                                    title="ترکیب کاراکترها"
                                    description="از حروف بزرگ، کوچک، اعداد و نمادها استفاده کنید."
                                    color="brand"
                                />
                                <SecurityTip
                                    icon={Lock}
                                    title="رمز یکتا"
                                    description="از رمز فعلی یا رمزهای سایر حساب‌ها استفاده نکنید."
                                    color="amber"
                                />
                            </div>
                        </div>

                        {/* کارت هشدار */}
                        <div className="bg-gradient-to-br from-amber-50 to-amber-50/50 border rounded-2xl border-amber-200 p-4">
                            <p className="text-[11px] font-bold text-amber-800 mb-1">
                                ⚠️ توجه
                            </p>
                            <p className="text-[10px] text-amber-700 leading-relaxed">
                                پس از تغییر رمز عبور، ممکن است نیاز به ورود مجدد داشته باشید.
                                رمز جدید را در جای امنی ذخیره کنید.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}